import type { Survey } from 'types';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';
import { toast } from '@/hooks/use-toast';

export type SurveyDraft = {
  name: string;
  type: Survey['type'];
  position: Survey['position'];
  triggerConfig: Survey['triggerConfig'];
  theme: Survey['theme'];
  frequencyCap: Survey['frequencyCap'];
  texts: Survey['texts'];
  /** URL and device rules, one per row as survey_targeting stores them. */
  targeting: Pick<Survey['targeting'][number], 'url' | 'condition' | 'device'>[];
  experimentId: number | null;
  variantIds: number[];
  sampleRate: number;
  sentimentEnabled?: boolean;
  questions: {
    kind: 'score' | 'open text' | 'single choice' | 'multiple choice';
    label: string;
    required: boolean;
    config?: Record<string, unknown>;
  }[];
};

/**
 * What start and end answer: the survey, whether the website publish that follows worked, and
 * (start only) whether the website serves anything at all.
 */
type SurveyTransition = Survey & { published?: boolean; served?: boolean };

const NOT_SERVED =
  "This website is paused (its plan is not active, or this month's sessions are used up), so visitors do not see the survey.";

const NOT_PUBLISHED =
  'The website could not be published just now, so visitors do not see this yet. Pertento has been alerted.';

export const useSurvey = (surveyId: number) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const { website } = useWebsites();
  const [isSaving, setIsSaving] = useState(false);

  const { data: survey, isLoading } = useQuery<Survey>({
    enabled: !!surveyId,
    queryKey: ['SURVEY', surveyId],
    queryFn: () => apiClient.get<Survey>(`/surveys/${surveyId}`),
  });

  const invalidate = (id?: number) => {
    queryClient.invalidateQueries({ queryKey: ['SURVEYS'] });
    if (id) queryClient.invalidateQueries({ queryKey: ['SURVEY', id] });
  };

  const createSurvey = async (draft: SurveyDraft) => {
    if (!website) return;
    setIsSaving(true);
    try {
      const created = await apiClient.post<Survey>('/surveys', { ...draft, websiteId: website.id });
      invalidate();
      return created;
    } finally {
      setIsSaving(false);
    }
  };

  const updateSurvey = async (id: number, draft: Partial<SurveyDraft>) => {
    setIsSaving(true);
    try {
      // Questions are replaced through their own route, because that endpoint
      // rejects a Running survey — rewriting questions would orphan the responses
      // already collected against their ids. Targeting has its own route too, and
      // that one is allowed while Running.
      const { questions, targeting, ...fields } = draft;
      await apiClient.put(`/surveys/${id}`, fields);
      if (targeting) await apiClient.put(`/surveys/${id}/targeting`, { targeting });
      if (questions) await apiClient.put(`/surveys/${id}/questions`, { questions });
      invalidate(id);
    } finally {
      setIsSaving(false);
    }
  };

  const startSurvey = async (id: number) => {
    const started = await apiClient.post<SurveyTransition>(`/surveys/${id}/start`, {});
    invalidate(id);
    // The route publishes the website as it starts the survey (PER-332). When that
    // rebuild fails, or the website serves nothing (no active plan, or its sessions
    // are used up), the status still changed, so the toast says it is not live.
    const description =
      started?.published === false
        ? NOT_PUBLISHED
        : started?.served === false
          ? NOT_SERVED
          : 'It is live on this website now.';
    toast({ title: 'Survey started', description });
  };

  const endSurvey = async (id: number) => {
    const ended = await apiClient.post<SurveyTransition>(`/surveys/${id}/end`, {});
    invalidate(id);
    // The same the other way round: ending publishes, so the widget goes at once.
    toast({
      title: 'Survey ended',
      description: ended?.published === false ? NOT_PUBLISHED : 'It no longer shows on this website.',
    });
  };

  const archiveSurvey = async (id: number) => {
    await apiClient.post(`/surveys/${id}/archive`, {});
    invalidate(id);
  };

  const duplicateSurvey = async (id: number) => {
    const copy = await apiClient.post<Survey>(`/surveys/${id}/duplicate`, {});
    invalidate();
    return copy;
  };

  const deleteSurvey = async (id: number) => {
    await apiClient.delete(`/surveys/${id}`);
    invalidate();
  };

  return {
    survey,
    isLoading,
    isSaving,
    createSurvey,
    updateSurvey,
    startSurvey,
    endSurvey,
    archiveSurvey,
    duplicateSurvey,
    deleteSurvey,
  };
};

export type SurveyManager = ReturnType<typeof useSurvey>;
